import {useLivePreview} from 'Application/useCases/useLivePreview';

import {isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {Header} from 'types/payload-types';

/**
 * Custom hook for managing header data with live preview functionality and logo extraction.
 * This hook provides real-time updates to header content from the CMS and safely extracts
 * the tenant's logo information when available. It handles the complexity of nested data
 * validation and ensures type safety throughout the header rendering process.
 *
 * @param headerData The initial header data from the CMS or static source.
 * @returns An object containing the live header data and extracted logo information.
 *
 * @example
 * ```tsx
 * const HeaderComponent = ({ initialHeaderData }) => {
 *     const { header, logo } = useHeader(initialHeaderData);
 *
 *     return (
 *         <header>
 *             {logo && <img src={logo.url} alt={logo.alt} />}
 *             <nav>{header.navigation}</nav>
 *         </header>
 *     );
 * };
 * ```
 */
export const useHeader = (headerData: Header) => {
    const header = useLivePreview<Header>({
        dataKey: 'Header',
        depth: 2,
        initialData: headerData
    });
    let logo;

    if (isDefinedAndPopulated(header.tenant) && isDefinedAndPopulated(header.tenant.logo)) {
        logo = header.tenant.logo;
    }

    return {
        header,
        logo
    };
};